
import { defineComponent } from 'vue';
import Form from '@/shared/mixins/Form';
import { estateAgentService } from '@/estate-agent/EstateAgentService';
import { required } from '@vuelidate/validators';
import store from '@/store';
import ResizableImage from '@/shared/components/ResizableImage.vue';
import { UploadFile } from '@/shared/models/UploadFile';
import Cropper from '@/shared/components/Cropper.vue';
import useVuelidate from '@vuelidate/core';
import PhonePrefix from '@/shared/components/PhonePrefix.vue';
import config from '@/shared/constants/config';
import phoneNumber from '@/shared/helpers/phoneNumberValidator';

export default defineComponent({
  name: 'EstateAgentProfileEdit',
  mixins: [Form],
  setup() {
    return { v$: useVuelidate() };
  },
  components: {
    ResizableImage,
    Cropper,
    PhonePrefix,
  },
  data() {
    return {
      estateAgentImage: {} as null | UploadFile,
      showEstateAgentImageModal: false,
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        photo: '',
        photoRelativeUrl: '',
        logo: '',
        logoRelativeUrl: '',
        description: '',
        motto: '',
        languages: [],
      },
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumberPrefix: config.DEFAULT_PHONE_PREFIX,
        phoneNumber: '',
        photo: '' as null | string,
        description: '',
        motto: '',
        languages: [],
      },
      formModel: {},
    };
  },
  validations() {
    return {
      form: {
        firstName: { required },
        lastName: { required },
        email: { required },
        phoneNumberPrefix: { required },
        phoneNumber: { required, phoneNumber: phoneNumber(this.form.phoneNumberPrefix) },
        description: {},
        motto: {},
      },
    };
  },
  async created() {
    this.isLoading = true;
    try {
      const result = await estateAgentService.getForm();
      this.formModel = result.data;
      this.profile = result.data.model;
      this.form = result.data.model;
      if (result.data.model.phoneNumberPrefix) {
        this.form.phoneNumberPrefix = config.PHONE_PREFIXES.find((x) => x.value === result.data.model.phoneNumberPrefix);
      }
      if (result.data.model.photo) {
        this.estateAgentImage = {} as UploadFile;
        this.estateAgentImage.file = result.data.model.photo;
        this.estateAgentImage.url = result.data.model.photoRelativeUrl;
        this.estateAgentImage.isSuccess = true;
      }
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    onEdit() {
      this.isSuccess = false;
      this.isError = false;
    },
    async send() {
      if (!this.validate()) return;

      try {
        const form = Object.assign({}, this.form);

        if (form.phoneNumberPrefix && form.phoneNumberPrefix.value) {
          form.phoneNumberPrefix = form.phoneNumberPrefix.value;
        }

        await estateAgentService.updateProfile(form);
        this.success(false);
        store.dispatch('userUpdate', { firstName: form.firstName, lastName: form.lastName });
        this.$router.push({ name: 'UserProfile' });
      } catch (error) {
        this.handleApiError(error);
      }
    },
    async handleEstateAgentImageUpload() {
      const ref = this.$refs.estateAgentImage as any;
      const files = ref.files as string[] | Blob[];
      const file = files[0];
      await this.uploadEstateAgentImage(file);
      ref.value = null;
    },
    async uploadEstateAgentImage(file: any) {
      this.showEstateAgentImageModal = false;
      this.estateAgentImage = {} as UploadFile;
      this.estateAgentImage.isUploading = true;
      this.estateAgentImage.uploadPercentage = 0;
      this.estateAgentImage.file = file.name;
      const formData = new FormData();
      formData.append('file', file, file.name);

      try {
        const result = await estateAgentService.uploadPhoto(formData, (progressEvent: any) => {
          if (this.estateAgentImage) {
            this.estateAgentImage.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100).toString());
          }
        });
        this.estateAgentImage.file = result.data.photo;
        this.estateAgentImage.url = result.data.url;
        this.estateAgentImage.isSuccess = true;
        this.form.photo = result.data.photo;
      } catch (error) {
        this.estateAgentImage.isError = true;
        this.estateAgentImage.text = 'Error';
        const errors = this.getApiErrors(error);
        this.estateAgentImage.text = errors[0].message;
      } finally {
        this.estateAgentImage.isUploading = false;
      }
    },
    editEstateAgentFile() {
      this.showEstateAgentImageModal = true;
    },
    deleteEstateAgentFile() {
      this.estateAgentImage = {} as UploadFile;
      this.form.photo = null;
    },
  },
});
